// src/pages/seeker/SeekerJobDetail.jsx

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { jobAdPublicAPI } from '../../services/api';
import { ArrowLeft, MapPin, Clock, Briefcase, Users, Globe } from 'lucide-react';

const formatAddress = (addr) => {
  if (!addr) return '';
  if (typeof addr === 'string') return addr;
  return [addr.suburb, addr.state, addr.country].filter(Boolean).join(', ');
};

const scheduleLabel = {
  FULL_TIME:   'Full-time',
  PART_TIME:   'Part-time',
  CASUAL:      'Casual',
  CONTRACT:    'Contract',
};

const arrangementLabel = {
  ONSITE: 'Onsite',
  HYBRID: 'Hybrid',
  REMOTE: 'Remote',
};

const salaryPeriodLabel = {
  HOURLY:  '/hr',
  DAILY:   '/day',
  WEEKLY:  '/wk',
  ANNUAL:  '/yr',
};

const seniorityLabel = {
  UNSPECIFIED:       '',
  VOLUNTEER:         'Volunteer',
  OFFICER:           'Officer',
  SUPERVISOR:        'Supervisor',
  ASSISTANT_MANAGER: 'Assistant Manager',
  MANAGER:           'Manager',
  SENIOR_MANAGER:    'Senior Manager',
  EXECUTIVE_MANAGER: 'Executive Manager',
};

export const SeekerJobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Use job passed via navigation state if available, else fetch
  const [ad, setAd]         = useState(location.state?.ad || null);
  const [loading, setLoading] = useState(!location.state?.ad);

  useEffect(() => {
    if (ad) return;
    jobAdPublicAPI.getById(id)
      .then(({ data }) => setAd(data.data.ad))
      .catch(() => navigate('/seeker/jobs', { replace: true }))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  if (loading) {
    return (
      <div className="fade-in">
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 4, padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!ad) return null;

  const initials = ad.groupProfile?.profileName?.[0]?.toUpperCase() || 'C';
  const closingDate = ad.closingDate ? new Date(ad.closingDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : null;

  return (
    <div className="fade-in">
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-muted)', fontSize: 12, fontWeight: 600,
          marginBottom: 16, padding: 0,
        }}
        onMouseEnter={e => e.currentTarget.style.color = '#3D80CC'}
        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
      >
        <ArrowLeft size={14} /> Back to jobs
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, alignItems: 'start' }}>

        {/* Left: main content */}
        <div>
          {/* Header */}
          <div className="section-box" style={{ marginBottom: 16 }}>
            <div className="section-box-body" style={{ padding: '20px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 10, background: 'var(--primary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, fontSize: 22, fontWeight: 700, color: 'white',
                }}>
                  {initials}
                </div>
                <div style={{ flex: 1 }}>
                  <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>{ad.title}</h1>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#3D80CC', marginBottom: 6 }}>
                    {ad.groupProfile?.profileName}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {ad.groupProfile?.industry   && <span className="tag tag-ghost">{ad.groupProfile.industry}</span>}
                    {ad.groupProfile?.companyType && <span className="tag tag-ghost">{ad.groupProfile.companyType}</span>}
                    {closingDate && (
                      <span className="tag tag-ghost" style={{ color: 'var(--text-muted)' }}>
                        Closes {closingDate}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          {ad.description && (
            <div className="section-box" style={{ marginBottom: 16 }}>
              <div className="section-box-header">About this Role</div>
              <div className="section-box-body" style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.7, color: 'var(--text)' }}>
                {ad.description}
              </div>
            </div>
          )}

          {/* Positions */}
          {ad.positions?.length > 0 && (
            <div className="section-box">
              <div className="section-box-header">
                {ad.positions.length === 1 ? 'Position Details' : `Positions (${ad.positions.length})`}
              </div>
              <div className="section-box-body" style={{ padding: 0 }}>
                {ad.positions.map((pos, idx) => (
                  <div key={pos.id} style={{
                    padding: '16px 20px',
                    borderBottom: idx < ad.positions.length - 1 ? '1px solid var(--border-lt)' : 'none',
                  }}>
                    {/* Position title */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
                          {pos.jobDefinition?.name || 'Position'}
                        </div>
                        {pos.jobDefinition?.seniorityLevel && pos.jobDefinition.seniorityLevel !== 'UNSPECIFIED' && (
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                            {seniorityLabel[pos.jobDefinition.seniorityLevel] || pos.jobDefinition.seniorityLevel}
                          </div>
                        )}
                      </div>
                      {pos.vacancies > 1 && (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', background: '#f5f5f5', padding: '3px 8px', borderRadius: 4 }}>
                          {pos.vacancies} vacancies
                        </span>
                      )}
                    </div>

                    {/* Tags row */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                      {pos.schedule       && <span className="tag tag-ghost">{scheduleLabel[pos.schedule]   || pos.schedule}</span>}
                      {pos.workArrangement && <span className="tag tag-ghost">{arrangementLabel[pos.workArrangement] || pos.workArrangement}</span>}
                      {pos.workplace?.address && (
                        <span className="tag tag-ghost" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <MapPin size={10} /> {formatAddress(pos.workplace.address)}
                        </span>
                      )}
                      {pos.workplace?.name && (
                        <span className="tag tag-ghost">{pos.workplace.name}</span>
                      )}
                    </div>

                    {/* Salary */}
                    {pos.salaryVisible && (pos.salaryMin || pos.salaryMax) && (
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#28A745', marginBottom: 8 }}>
                        {pos.salaryMin && pos.salaryMax
                          ? `$${pos.salaryMin.toLocaleString()} – $${pos.salaryMax.toLocaleString()}`
                          : pos.salaryMin
                          ? `From $${pos.salaryMin.toLocaleString()}`
                          : `Up to $${pos.salaryMax.toLocaleString()}`}
                        {pos.salaryPeriod && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> {salaryPeriodLabel[pos.salaryPeriod]}</span>}
                        {pos.salaryCurrency && pos.salaryCurrency !== 'AUD' && (
                          <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 4 }}>{pos.salaryCurrency}</span>
                        )}
                      </div>
                    )}

                    {/* Languages */}
                    {pos.languages?.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                        <Globe size={12} />
                        Languages: {Array.isArray(pos.languages) ? pos.languages.join(', ') : pos.languages}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: sidebar */}
        <div>
          {/* Apply button placeholder */}
          <div className="section-box" style={{ marginBottom: 12 }}>
            <div className="section-box-body">
              <button className="btn btn-primary" style={{ width: '100%', marginBottom: 8 }} disabled>
                Apply Now
              </button>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                Applications coming soon
              </div>
            </div>
          </div>

          {/* Contact */}
          {ad.contact && (
            <div className="section-box" style={{ marginBottom: 12 }}>
              <div className="section-box-header">Contact</div>
              <div className="section-box-body">
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{ad.contact.name}</div>
                {ad.contact.position && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{ad.contact.position}</div>
                )}
                {ad.contact.email && (
                  <div style={{ fontSize: 12, color: '#3D80CC', marginTop: 4 }}>
                    <a href={`mailto:${ad.contact.email}`} style={{ color: '#3D80CC' }}>{ad.contact.email}</a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Company summary */}
          {ad.groupProfile?.about && (
            <div className="section-box">
              <div className="section-box-header">About the Company</div>
              <div className="section-box-body" style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.6 }}>
                {ad.groupProfile.about.length > 300
                  ? ad.groupProfile.about.slice(0, 300) + '…'
                  : ad.groupProfile.about}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
